import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';

import useEvent from '@src/hooks/useEvent';
import { cleanEventCall, setEventCountdownForSale } from '@src/store/actions/event';
import { isPublished, getShouldShowSoldOut, getTicketStatus } from '@src/utils/event';
import { STATE_SUCCESS, STATE_FAIL } from '@src/const/common';
import { formatTimeFloor } from '@src/utils/time';
import ErrorPage from '@src/components/common/errorPage';
import { sentGTMViewItem } from '@src/utils/gtag';

import BasicInfo from './basicInfo';
import AdvancedInfo from './advancedInfo';
import './global.module.less';

const EventPageBackground = styled.div`
	background: #FFFFFF;
`;

const EventWrapper = styled.div`
	padding: 0;

	@media ${props => props.theme.device.lgDesktop} {
		width: 1200px;
		padding: 16px 20px 40px;
		margin: 0 auto;
	}

	@media ${props => props.theme.device.desktop} {
		padding: 16px 0 0;
	}

	@media ${props => props.theme.device.mobile} {
		position: relative;
		top: 52px; // Header height
		background: #ffffff;
	}
`;

const EventPage = (): JSX.Element => {
	const dispatch = useDispatch();
	const { event, eventState, event: { publish_status }, sortedEventSlots } = useEvent();
	const slots = event?.slots;
	const isSoldOut = getShouldShowSoldOut({ sortedSlots: sortedEventSlots });

	// Bugfix for TEACHES-1918 temporarily.
	useEffect(() => () => {
		dispatch(cleanEventCall());
	}, []);

	useEffect(() => {
		if (!isPublished(publish_status) && eventState === STATE_SUCCESS) {
			window.location.href = window.location.origin;
		}
	}, [publish_status, eventState]);

	useEffect(() => {
		if (!isSoldOut) {
			const interval = setInterval(() => {
				const { countDownTime } = getTicketStatus({ slots });
				const endTimeSeconds = dayjs(countDownTime).valueOf();
				const startTimeSeconds = dayjs(new Date()).valueOf();
				const remainingSeconds = endTimeSeconds - startTimeSeconds;
				const countdown = formatTimeFloor(remainingSeconds);
				dispatch(setEventCountdownForSale({ countdown }));
			}, 1000);

			return () => {
				clearInterval(interval);
				dispatch(setEventCountdownForSale({ countdown: formatTimeFloor(0) }));
			};
		}
	}, [slots, isSoldOut]);

	useEffect(() => {
		sentGTMViewItem(event);
	}, [event]);

	return isEmpty(event) ? null : (
		(eventState === STATE_FAIL) ? <ErrorPage /> : (
			<EventPageBackground>
				<EventWrapper>
					<BasicInfo />
					<AdvancedInfo />
				</EventWrapper>
			</EventPageBackground>
		)
	);
};

export default EventPage;
